import type { DateFragment } from "@tento-chrono";
import type { Option } from "@tento-lona/sheets";

/**
 * How one calendar days strip bind ends once its provider reads have
 * delivered. DOM-free, so the package's own harness tests it without an
 * element (the DOM test shim cannot construct a strip).
 *
 * A bind hides the strip until its reads are painted. Every layer settles
 * before the strip is revealed, succeeded or failed: one rejected provider
 * read must not leave the layers that did paint invisible. A failed read is
 * reported and its completed-read ticket retired, so the next bind (any
 * invalidation or rebind) reads again instead of reusing it; it is never
 * marked complete.
 */

/**
 * Whether a span layer whose read for `failing` rejected may keep its last
 * paint: only when that paint was for the same fragment (same windowed
 * range). Layers are reused across dates, so any other paint is another
 * date's cards and must not be revealed.
 */
export function failedLayerKeepsPaint(
  shown: Option<DateFragment.Windowed>,
  failing: DateFragment.Windowed,
): boolean {
  if (shown == null) return false;
  if (shown === failing) return true;
  const a = shown.applyAll(), b = failing.applyAll();
  return a.start.mse === b.start.mse && a.end.mse === b.end.mse;
}

/** What a strip bind does when its reads settle. */
export type StripReadSettlement = {
  /** Whether the bind still owns the strip (same generation, connected). */
  readonly isCurrent: () => boolean;
  /** Queue `write` after every layer's positioning write (`$$.mutateEnd`). */
  readonly afterWrites: (write: () => void) => void;
  /** Show the strip and end its presentation. */
  readonly reveal: () => void;
  /** Mark the bind's read complete, reusable at the same revision. */
  readonly complete: () => void;
  /**
   * Retire the bind's read and report why; runs once, before the reveal.
   * `current` says whether the bind still owns the strip: a superseded
   * bind's failure is history (its successor reads again), not an alarm.
   */
  readonly fail: (errors: readonly string[], current: boolean) => void;
};

/**
 * Wait for every layer's asynchronous read `jobs` (never only the first
 * failure), then report any failures (`failures`: synchronous layers that
 * already failed, plus rejected jobs) and reveal the strip after the layers'
 * writes if the bind is still current. With no asynchronous job the outcome
 * is decided synchronously, so a strip whose layers all painted in this write
 * task reveals in this frame's end-write phase. Resolves once the outcome is
 * decided; the reveal itself runs in the queued write.
 */
export async function settleStripReads(
  jobs: readonly Promise<void>[],
  failures: readonly string[],
  settlement: StripReadSettlement,
): Promise<void> {
  const errors = [...failures];
  if (jobs.length > 0) {
    const results = await Promise.allSettled(jobs);
    for (const result of results) {
      if (result.status === "rejected") errors.push(String(result.reason));
    }
  }
  if (errors.length > 0) settlement.fail(errors, settlement.isCurrent());
  settlement.afterWrites(() => {
    if (!settlement.isCurrent()) return;
    settlement.reveal();
    if (errors.length === 0) settlement.complete();
  });
}
